import * as React from "react";
import { useMemo, useState, useCallback } from "react";
import * as Immutable from "immutable";
import styled from "styled-components";
import SectionComponent from "components/common/Section/SectionComponent";
import { Table } from "components/bootstrap";
import { PaginatedItemOverview, Spinner } from "components/common";
import { fetchPaginatedMembershipPolicies } from "collections/hooks/useMembershipPolicies";
import { DEFAULT_PAGINATION } from "components/common/PaginatedItemOverview";
import EntityCollectionItem from "collections/components/CollectionMembershipConfig/EntityCollectionItem";
import useMembershipPoliciesMutations from "collections/hooks/useMembershipPoliciesMutations";
import CollectionsSelector from "collections/components/CollectionMembershipConfig/CollectionsSelector";
import UserNotification from "util/UserNotification";
import useLicenseValidityForSubject from "license/hooks/useLicenseValidityForSubject";
import { LICENSE_SUBJECTS } from "license/constants";
import { COLLECTIONS_ENABLED } from "collections/constants";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
const CollectionRenderer = ({ item, onDeleteItem }) => /* @__PURE__ */ React.createElement(EntityCollectionItem, { item, onDeleteItem: onDeleteItem != null ? onDeleteItem : void 0 });
const getCollectionWrapper = (shouldShowActions = false) => ({ isEmptyResult, children }) => isEmptyResult ? /* @__PURE__ */ React.createElement("div", null, children) : /* @__PURE__ */ React.createElement(Table, { condensed: true }, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("th", null, i18n.t("membership_config.collection_name", { ns: "enterprise_collections" })), /* @__PURE__ */ React.createElement("th", null, i18n.t("shared.description", { ns: "enterprise_collections" })), shouldShowActions && /* @__PURE__ */ React.createElement("th", null, i18n.t("membership_config.actions", { ns: "enterprise_collections" })))), /* @__PURE__ */ React.createElement("tbody", null, children));
const Container = styled.div`
  margin-top: 15px;
  margin-bottom: 15px;
`;
const CollectionMembershipConfigSection = ({ entityId, entityType, readOnly = false }) => {
  const { t } = useTranslation("enterprise_collections");
  const [loading, setLoading] = useState(false);
  const [paginatedMembershipPolicies, setPaginatedMembershipPolicies] = useState();
  const { removeMembershipPolicy, createMembershipPolicy } = useMembershipPoliciesMutations();
  const {
    data: { valid: isValidLicense },
    isInitialLoading: isLoadingLicense
  } = useLicenseValidityForSubject(LICENSE_SUBJECTS.enterprise);
  const searchParams = useMemo(
    () => ({
      page: 1,
      pageSize: 10,
      query: `initiator_id:${entityId}`,
      sort: { attributeId: "initiator_id", direction: "asc" }
    }),
    [entityId]
  );
  const onLoad = useCallback(
    (pagination = DEFAULT_PAGINATION) => {
      setLoading(true);
      return fetchPaginatedMembershipPolicies({
        ...searchParams,
        pageSize: pagination.perPage,
        page: pagination.page
      }).then((paginatedMembershipPoliciesResult) => {
        setLoading(false);
        const { list, pagination: responsePagination } = paginatedMembershipPoliciesResult;
        const policiesItems = Immutable.List(
          list.map((policy) => ({ id: policy.id, name: policy.target_id, description: policy.target_type }))
        );
        return {
          list: policiesItems,
          pagination: responsePagination
        };
      });
    },
    [searchParams]
  );
  const reload = () => onLoad().then(setPaginatedMembershipPolicies);
  const handleDelete = (item) => {
    removeMembershipPolicy({ id: item.id }).then(() => {
      reload();
    });
  };
  const handleSubmit = (collectionIds) => {
    const requests = collectionIds.map(
      (id) => createMembershipPolicy({
        entity: {
          initiator_type: entityType,
          initiator_id: entityId,
          target_type: "collection",
          target_id: id
        }
      })
    );
    Promise.allSettled(requests).then(() => {
      UserNotification.success(t("membership_config.update_success"), t("shared.success"));
      reload();
    }).catch(() => UserNotification.error(t("membership_config.update_error"), t("shared.success")));
  };
  if (!COLLECTIONS_ENABLED) return null;
  if (isLoadingLicense) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  return isValidLicense && /* @__PURE__ */ React.createElement(SectionComponent, { title: t("membership_config.title"), showLoading: loading }, /* @__PURE__ */ React.createElement(Container, null, !readOnly && loading && /* @__PURE__ */ React.createElement(Spinner, null), !readOnly && /* @__PURE__ */ React.createElement(CollectionsSelector, { entityId, onSubmit: handleSubmit, entityType }), /* @__PURE__ */ React.createElement(
    PaginatedItemOverview,
    {
      noDataText: t("membership_config.no_data"),
      onLoad,
      disableSearchFilter: true,
      resultsWrapperComponent: getCollectionWrapper(),
      overrideItemComponent: CollectionRenderer,
      overrideList: paginatedMembershipPolicies,
      onDeleteItem: !readOnly ? handleDelete : null
    }
  )));
};
export default CollectionMembershipConfigSection;
